import { LitElement, PropertyValues, css, html, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import '@spectrum-web-components/bundle/elements.js'

import './day-of-week/day-of-week'
import './time-range/time-range'
import { OpeningHours } from '../types/opening-hours'
import { API_ENDPOINTS } from '../../../config/api.config'
import { ApiService } from '../../../utils/api'
import { HolidayTimeEntry } from '../../../utils/types/api-types'

export class HolidayHours extends LitElement {

  @property({type: Array}) currentHours: OpeningHours[] = []
  @property({type: Array}) holidays: any[] = []

  @state() editModeForHolidays: Record<string, boolean> = {}

  
  @property({type: Number}) restaurantId: number | undefined
  @property({type: String}) key: string = ''

  private holidayKey(name: string, date: string): string {
    return `${name}__${date}`
  }

  private sameHoliday(a: any, b: any): boolean {
    return a.name === b.name && a.validFrom === b.validFrom
  }

  private mergeOvernightHolidayEntries(holidays: any[]): any[] {
    const consumed = new Set<number>()
    const combined: any[] = []

    holidays.forEach((holiday, index) => {
      if (consumed.has(index)) return

      const nextDay = new Date(holiday.validFrom)
      nextDay.setDate(nextDay.getDate() + 1)
      const nextDayString = nextDay.toISOString().split('T')[0]

      const nextDayIndex = holidays.findIndex((h, candidateIndex) =>
        candidateIndex !== index &&
        !consumed.has(candidateIndex) &&
        h.name === holiday.name &&
        h.validFrom === nextDayString &&
        h.opens === '00:00' &&
        h.closes !== '00:00'
      )

      if (nextDayIndex >= 0 && holiday.closes === '23:59') {
        consumed.add(nextDayIndex)
        combined.push({
          ...holiday,
          closes: holidays[nextDayIndex].closes,
        })
      } else {
        combined.push(holiday)
      }
    })

    return combined
  }

  protected async firstUpdated(_changedProperties: PropertyValues): Promise<void> {
    if (!this.restaurantId) {
      console.error('Restaurant ID is required for holiday hours')
      return
    }
    
    try {
      const numericId = parseInt(this.restaurantId.toString(), 10)
      const auth = { restaurantId: this.restaurantId.toString(), key: this.key }
      const data = await ApiService.get<HolidayTimeEntry[]>(API_ENDPOINTS.holidaysTimes(numericId), auth)

      console.log('Fetched holidays:', data)

      // Transform the response into the current holidays format
      this.holidays = data.map((holiday: any) => ({
        validFrom: holiday.date,
        validThrough: holiday.date,
        opens: holiday.opentime,
        closes: holiday.closetime,
        name: holiday.name
      }))

      this.holidays = this.mergeOvernightHolidayEntries(this.holidays)
      console.log('Fetched and transformed this.holidays:', this.holidays)

    } catch (error) {
      console.error('Error fetching holiday hours:', error)
    }
  }

  toggleEditMode(holidayId: string) {
    this.editModeForHolidays = {
      ...this.editModeForHolidays,
      [holidayId]: !this.editModeForHolidays[holidayId]
    }
  }

  /**
   * Saves holiday hours for a specific holiday.
   *
   * The data flow is:
   * 1. Read time slots from DOM
   * 2. Build entries for this holiday (respecting closed/open state)
   * 3. Combine with other holidays (unchanged)
   * 4. Split overnight hours for API
   * 5. Send to API
   */
  save(holidayId: string) {
    if (!this.restaurantId) {
      console.error('Restaurant ID is required to save holiday hours')
      return
    }

    const timeElements = Array.from(this.shadowRoot?.querySelectorAll('time-range') || [])
      .filter(el => el.getAttribute('data-holiday-key') === holidayId)

    if (!timeElements.length) return

    // Get the date for this holiday from existing data
    const existingHoliday = this.holidays.find(h => this.holidayKey(h.name, h.validFrom) === holidayId)
    if (!existingHoliday) return
    const holidayName = existingHoliday.name
    const holidayDate = existingHoliday.validFrom

    // Step 1: Build time slots for this holiday
    const editedTimeSlots = this.getTimeSlotsFromDOM(timeElements as Element[])

    // Step 2: Build updated holidays list
    // - Keep other holidays unchanged
    // - Replace this holiday's entries with new ones
    const otherHolidays = this.holidays.filter(h => this.holidayKey(h.name, h.validFrom) !== holidayId)
    const thisHolidayEntries = editedTimeSlots.map(slot => ({
      name: holidayName,
      validFrom: holidayDate,
      validThrough: holidayDate,
      opens: slot.opens,
      closes: slot.closes
    }))
    const updatedHolidays = [...otherHolidays, ...thisHolidayEntries]

    // Step 3: Format for API and split overnight hours
    const apiHolidays = updatedHolidays.flatMap(h => this.formatHolidayForApi(h))

    const payload = {
      restaurantId: Number(this.restaurantId),
      holidays: apiHolidays
    }

    console.log('Saving holiday hours:', JSON.stringify(payload))

    const auth = { restaurantId: this.restaurantId.toString(), key: this.key }
    ApiService.post(API_ENDPOINTS.updateHolidaysTimes(), payload, auth)
      .then(data => {
        console.log('Holiday hours saved:', data)
        this.holidays = updatedHolidays
        this.dispatchEvent(new CustomEvent('holidayhourschange', { detail: updatedHolidays }))
        this.toggleEditMode(holidayId)
      })
      .catch(error => {
        console.error('Error saving holiday hours:', error)
      })
  }

  /**
   * Reads time slots from DOM elements.
   * Returns either:
   * - [{opens: '00:00', closes: '00:00'}] if toggle is closed
   * - Array of non-empty time slots if toggle is open
   */
  private getTimeSlotsFromDOM(timeElements: Element[]): { opens: string, closes: string }[] {
    const isClosed = timeElements[0]?.hasAttribute('off')

    if (isClosed) {
      return [{ opens: '00:00', closes: '00:00' }]
    }

    // Collect all non-empty time slots
    const slots: { opens: string, closes: string }[] = []
    for (const el of timeElements) {
      const opens = el.getAttribute('from') ?? '00:00'
      const closes = el.getAttribute('to') ?? '00:00'

      // Skip empty slots (00:00-00:00 means "no time slot")
      const isEmpty = opens === '00:00' && closes === '00:00'
      if (!isEmpty) {
        slots.push({ opens, closes })
      }
    }

    return slots
  }

  /**
   * Formats a holiday for the API.
   * Splits overnight hours into two entries (one ending at 23:59, one starting at 00:00 next day).
   */
  private formatHolidayForApi(holiday: any): { name: string, date: string, opentime: string, closetime: string }[] {
    const { name, validFrom: date, opens: opentime } = holiday
    const closetime = holiday.closes === '24:00' ? '23:59' : holiday.closes

    const toMinutes = (time: string) => {
      const [h, m] = time.split(':').map(Number)
      return h * 60 + m
    }

    const opensMinutes = toMinutes(opentime)
    const closesMinutes = toMinutes(closetime)
    const isClosed = opensMinutes === 0 && closesMinutes === 0
    const isOvernight = closesMinutes < opensMinutes

    // Closed or normal hours: single entry
    if (isClosed || !isOvernight) {
      return [{ name, date, opentime, closetime }]
    }

    // Overnight hours: split into two days
    const nextDate = new Date(date)
    nextDate.setDate(nextDate.getDate() + 1)
    const nextDateStr = nextDate.toISOString().split('T')[0]

    return [
      { name, date, opentime, closetime: '23:59' },
      { name, date: nextDateStr, opentime: '00:00', closetime }
    ]
  }

  render() {
    let rendered: any[] = []

    return html`
      <h2>Feiertage
        <sp-action-button size="xs" quiet>
          <sp-icon-info slot="icon"></sp-icon-info>
          <sp-tooltip self-managed placement="right">
            Für Feiertage sind die Öffnungszeiten für Sonntag voreingestellt,<br />
            können aber individuell angepasst werden.
          </sp-tooltip>
        </sp-action-button>
      </h2>

      ${this.holidays?.map((o, i) => {
        const uniqueKey = `${o.name}-${o.validFrom}`
        if(rendered.includes(uniqueKey)) return
        rendered.push(uniqueKey)
        const holidayId = this.holidayKey(o.name as string, o.validFrom as string)
        const editmode = this.editModeForHolidays[holidayId] || false
        const date = o.validFrom ? new Date(o.validFrom) : new Date('2025-01-01')
        const formattedDate = new Intl.DateTimeFormat("de-DE", { day: "numeric", month: "numeric", year: "numeric" }).format(date)

        const holidaySlots = this.holidays
          .filter(h => this.sameHoliday(h, o))
          .map(h => ({
            opens: h.opens,
            closes: h.closes || '00:00'
          }))
          .sort((a, b) => a.opens.localeCompare(b.opens))

        const editableSlots = holidaySlots.length > 1
          ? holidaySlots
          : [...holidaySlots, { opens: '00:00', closes: '00:00' }]

        return html`
          <section class="holiday emphasized" id=${o.name || 'holiday' + i}>
            <dl>
              <dt>${ o.name }  ${formattedDate}</dt>
              ${!editmode ? html`
                <dd><day-of-week .name=${o.dayOfWeek}></day-of-week>
                ${holidaySlots.every(slot => slot.opens === '00:00' && slot.closes === '00:00')
                  ? html`Geschlossen`
                  : html`
                    ${holidaySlots.map(slot => slot.opens !== '00:00' || slot.closes !== '00:00'
                      ? html`${slot.opens}-${slot.closes} `
                      : nothing)}
                  `
                }
              </dd>
              ` : nothing}
            </dl>
            ${editmode ? html`
              <time-range
                from=${ editableSlots[0].opens}
                to=${ editableSlots[0].closes}
                name=${ o.name as string }
                data-holiday-key=${ holidayId }
                switch></time-range>
              ${editableSlots.slice(1).map(slot => html`
                <time-range
                  from=${ slot.opens}
                  to=${ slot.closes}
                  name=${ o.name as string }
                  data-holiday-key=${ holidayId }
                  clear></time-range>
              `)}
              <br /><br />
              <sl-button @click=${() => this.toggleEditMode(holidayId)}>Abbrechen</sl-button>
              <sl-button variant="primary" @click=${() => this.save(holidayId)}>Speichern</sl-button>
            ` : html`
              <sl-button variant="primary" @click=${() => this.toggleEditMode(holidayId)}>Anpassen</sl-button>
            `}
          </section>
        `
      })}
    `
  }

  static styles = css`
    sp-icon-info {
      color: #5483d0;
    }

    section.emphasized {
      background-color: #fff;
      text-align: center;
      padding: 30px;
      border-radius: var(--sl-border-radius-medium);
      margin-bottom: 20px;
      filter: drop-shadow(.5px 1px 1px hsla(0, 0%, 54%, 0.15)) drop-shadow(1px 2px 2px hsl(285deg 40% 40%/.15)) drop-shadow(2px 4px 4px hsla(270, 1%, 49%, 0.15)) drop-shadow(4px 8px 8px hsla(270, 1%, 50%, 0.15)) ;
    }

    dl {
      margin-top: 0;
      margin-bottom: 30px;
    }

    dl dd {
      margin-left: 0;
    }

    section {
      min-width: min-content;
      margin: 25px auto 50px auto;
    }

    section.holiday time-range {
      max-width: max-content;
      margin: 0 auto;
    }

    ul {
      padding: 0;
      margin-bottom: 30px;
    }

    h1, h2, h3, h4, h5 {
      margin-top: 0;
      padding-left: 42px;
      text-align: center;
      font-family: 'Asap Condensed';
    }

  `
}

if (!customElements.get('holiday-hours')) {
  customElements.define('holiday-hours', HolidayHours)
}

declare global {
  interface HTMLElementTagNameMap {
    'holiday-hours': HolidayHours
  }
}
